import { create } from 'zustand';

/**
 * Live visibility into the write-through persistence layer (`api/persist.ts`).
 *
 * Persistence is optimistic — a failed write leaves local state standing — but
 * it must not be *silent*. This tiny store tracks in-flight writes and their
 * outcome so a top-bar indicator can show `saving` / `saved` / `offline` instead
 * of the old `void fetch(...).catch(() => {})` black hole.
 *
 * Ephemeral by design: this is live session telemetry, never persisted.
 */
export type PersistenceStatus = 'idle' | 'saving' | 'saved' | 'offline' | 'rejected' | 'error';

/**
 * How one write settled (mirrors persist.ts `PersistOutcome`):
 * - 'ok'        — 2xx.
 * - 'transient' — network / abort / 5xx: the API is unreachable or faulting
 *                 (the amber "offline" chip; the write queue retries).
 * - 'rejected'  — the server answered and REFUSED it (409 / 422 / any other 4xx):
 *                 the API is up, so this is never "offline" (tv-2na.8.5). Carries
 *                 the server's reason for the chip.
 */
export type SettleOutcome = 'ok' | 'transient' | 'rejected';

export interface PersistenceStatusState {
  status: PersistenceStatus;
  /** Number of writes currently awaiting a response. */
  inFlight: number;
  /** The server's reason for the last refusal (status 'rejected' only). */
  reason?: string;
  /** A write has been dispatched. */
  begin: () => void;
  /** A write settled with this outcome (`reason`: the server's refusal message). */
  settle: (outcome: SettleOutcome, reason?: string) => void;
  /** Attach the server's reason to a refusal once its body has parsed. */
  setRejectReason: (reason: string) => void;
  /** A write genuinely failed (timeout / network / 5xx) and we're surfacing it
   *  loudly. Distinct from `settle('transient')`'s quiet `offline`: this drives the red
   *  "write failed" chip and pairs with a toast. */
  fail: () => void;
  /** Clear back to idle (used by tests and on fresh session). */
  reset: () => void;
}

export const usePersistenceStatus = create<PersistenceStatusState>((set) => ({
  status: 'idle',
  inFlight: 0,
  begin: () => set((s) => ({ status: 'saving', inFlight: s.inFlight + 1 })),
  settle: (outcome, reason) =>
    set((s) => {
      const inFlight = Math.max(0, s.inFlight - 1);
      if (outcome === 'transient') return { status: 'offline', inFlight, reason: undefined };
      if (outcome === 'rejected') return { status: 'rejected', inFlight, reason };
      return { status: inFlight === 0 ? 'saved' : 'saving', inFlight, reason: undefined };
    }),
  setRejectReason: (reason) =>
    set((s) => (s.status === 'rejected' ? { reason } : {})),
  fail: () =>
    set((s) => ({ status: 'error', inFlight: Math.max(0, s.inFlight - 1) })),
  reset: () => set({ status: 'idle', inFlight: 0, reason: undefined }),
}));
